import CustomLoadingButton from 'Components/CustomLoadingButton'
import ServiceProviderFieldErrors from 'Components/ServiceProviderFieldErrors'
import StyledForm from 'Components/StyledForm/StyledForm'
import WithLoader from 'Components/WithLoader/WithLoader'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import {
  useCreateSingleDeviceMutation,
  useUpdateSingleDeviceMutation
} from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import type { Device } from 'Services/ServiceProviderManagement/Features/Device/types'
import { useGetAllSalesPointsQuery } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import type {
  ServiceProviderManagementError,
  ServiceProviderManagementViolation
} from 'Util/ApiClient'
import { IS_WALTTI_SERVICE_ENVIRONMENT, SORT_ORDER_ASC } from 'Util/constants'
import { formatViolationErrorField } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Autocomplete, Box, Grid, MenuItem, TextField } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  device_id: [],
  name: [],
  sales_point_id: [],
  source: [],
  directory_alias: [],
  machine_uuid: []
}

interface DeviceFormProps {
  device?: Device
  successBaseUrl: string
}

export default function DeviceForm({ device, successBaseUrl }: Readonly<DeviceFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!device

  const [createSingleDevice, createStatus] = useCreateSingleDeviceMutation()
  const [updateSingleDevice, updateStatus] = useUpdateSingleDeviceMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const {
    data: { resources: allSalesPoints } = { resources: [] },
    isLoading: isAllSalesPointsLoading
  } = useGetAllSalesPointsQuery(
    {
      sort_by: 'name',
      sort_order: SORT_ORDER_ASC
    },
    { refetchOnMountOrArgChange: true }
  )
  const salesPointOptions = allSalesPoints.map(salesPoint => {
    return {
      value: salesPoint.sales_point_id,
      label: salesPoint.name
    }
  })

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [deviceDetails, setDeviceDetails] = useState<Partial<Device>>(
    (isEdit && device) || {
      active: true
    }
  )

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    deviceDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    deviceDetails?.client_write ?? []
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined | null,
    field: keyof Device
  ) => {
    if (value !== undefined) {
      setDeviceDetails(prevState => ({ ...prevState, [field]: value }) as Device)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const deviceData = {
      ...deviceDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleDevice({
          ...deviceData,
          id: device.device_id
        })
      : createSingleDevice(deviceData)

    mutation
      .unwrap()
      .then((deviceResponse: Device) => {
        SnackbarUtils.success(t`Device successfully saved.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_DEVICE_PARAMETER_KEY]: deviceResponse.device_id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the device.`)
        }
      })
  }

  function setValidationErrors(violations: ServiceProviderManagementViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  return (
    <WithLoader loading={isAllSalesPointsLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box sx={{ display: 'flex' }}>
              <Box>
                <TextField
                  name="name"
                  required
                  label={t`Name`}
                  size="small"
                  sx={{ mr: 3 }}
                  value={deviceDetails?.name}
                  slotProps={{ htmlInput: { maxLength: 40 } }}
                  error={fieldErrors.name.length > 0}
                  onChange={e => handleChange(e.target.value, 'name')}
                />
                <ServiceProviderFieldErrors error={fieldErrors.name} identifier="name" />
              </Box>
              <Box>
                <TextField
                  name="device_id"
                  required
                  label={t`Device id`}
                  size="small"
                  sx={{ mr: 3 }}
                  value={deviceDetails?.device_id}
                  slotProps={{ htmlInput: { maxLength: 9 } }}
                  error={fieldErrors.device_id.length > 0}
                  onChange={e => handleChange(e.target.value, 'device_id')}
                />
                <ServiceProviderFieldErrors error={fieldErrors.device_id} identifier="device_id" />
              </Box>
              <Box>
                <Autocomplete
                  disablePortal
                  size="small"
                  options={salesPointOptions}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.value}>
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={params => (
                    <TextField required {...params} label={t`Device's sales point`} />
                  )}
                  onChange={(_, newValue) =>
                    handleChange(newValue?.value as string, 'sales_point_id')
                  }
                  value={
                    salesPointOptions.find(
                      option => option.value === deviceDetails.sales_point_id
                    ) || null
                  }
                  sx={{
                    minWidth: 280
                  }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.sales_point_id}
                  identifier="sales_point_id"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Box>
                <TextField
                  name="source"
                  label={t`Source`}
                  size="small"
                  sx={{ mr: 3 }}
                  value={deviceDetails?.source}
                  error={fieldErrors.source.length > 0}
                  onChange={e => handleChange(e.target.value, 'source')}
                  slotProps={{ htmlInput: { maxLength: 256 } }}
                />
                <ServiceProviderFieldErrors error={fieldErrors.source} identifier="source" />
              </Box>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <>
                  <Box>
                    <TextField
                      name="directory_alias"
                      label={t`Directory alias`}
                      size="small"
                      sx={{ mr: 3 }}
                      value={deviceDetails?.directory_alias}
                      error={fieldErrors.directory_alias.length > 0}
                      onChange={e => handleChange(e.target.value, 'directory_alias')}
                      slotProps={{ htmlInput: { maxLength: 256 } }}
                    />
                    <ServiceProviderFieldErrors
                      error={fieldErrors.directory_alias}
                      identifier="directory_alias"
                    />
                  </Box>
                  <Box>
                    <TextField
                      name="machine_uuid"
                      label={t`Machine uuid`}
                      size="small"
                      value={deviceDetails?.machine_uuid}
                      error={fieldErrors.machine_uuid.length > 0}
                      onChange={e =>
                        // Empty string is not allowed value, set to null in that case
                        handleChange(e.target.value === '' ? null : e.target.value, 'machine_uuid')
                      }
                      slotProps={{ htmlInput: { maxLength: 256 } }}
                    />
                    <ServiceProviderFieldErrors
                      error={fieldErrors.machine_uuid}
                      identifier="machine_uuid"
                    />
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ maxWidth: '750px', py: 2 }}>
              <SPMAllowedApiClients
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save device`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
